import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  X,
  Building,
  MapPinned,
  CheckCircle,
  Car,
  Clock,
  ChevronDown,
  ChevronRight,
  Plus,
} from "lucide-react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import useDebounce from "../hook/useDebounce";
import ErrorAlert from "./ui/ErrorAlert";

// CountsCard component remains unchanged
const CountsCard = ({ counts, loading }) => {
  const { branches_count, locations_count, slots_count } = counts || {};
  const client_count = counts?.total_client_count || 0;

  const renderCard = (icon, count, label, gradientClass) => (
    <div className="col-lg-3 col-md-6 col-sm-12">
      <div className={`card shadow-none border ${gradientClass} h-100`}>
        <div className="card-body p-20">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <p className="fw-medium text-primary-light mb-1">{label}</p>
              {loading ? (
                <div className="placeholder-glow">
                  <span className="placeholder col-6"></span>
                </div>
              ) : (
                <h6 className="mb-0">{count || 0}</h6>
              )}
            </div>
            <div className="w-50-px h-50-px bg-primary-600 rounded-circle d-flex justify-content-center align-items-center">
              {loading ? (
                <div className="placeholder-glow">
                  <span className="placeholder"></span>
                </div>
              ) : (
                <Icon icon={icon} className="text-white text-2xl mb-0" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="row g-20 mb-24">
      {renderCard(
        "solar:user-rounded-bold-duotone",
        client_count,
        "Total Clients",
        "bg-gradient-start-1"
      )}
      {renderCard(
        "solar:buildings-bold-duotone",
        branches_count,
        "Total Branches",
        "bg-gradient-start-2"
      )}
      {renderCard(
        "fluent:location-ripple-20-regular",
        locations_count,
        "Total Locations",
        "bg-gradient-start-3"
      )}
      {renderCard(
        "streamline:parking-sign-remix",
        slots_count,
        "Total Slots",
        "bg-gradient-start-4"
      )}
    </div>
  );
};

const ClientManagement = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [counts, setCounts] = useState({
    branches_count: 0,
    locations_count: 0,
    slots_count: 0,
  });
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [expandedClient, setExpandedClient] = useState({});
  const [expandedBranch, setExpandedBranch] = useState({});
  const [expandedLocation, setExpandedLocation] = useState({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);
  const [error, setError] = useState(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const perPage = 10;

  const handleHover = (locationId, type) => {
    setHoveredItem({ id: locationId, type });
  };

  const handleLeave = () => {
    setHoveredItem(null);
  };

  const fetchClients = async (pageNum, reset = false) => {
    if (loadingClients || loadingMore) return;
    setError(null);
    setLoadingClients(pageNum === 1);
    setLoadingMore(pageNum > 1);
    try {
      const response = await apiCall(
        "get",
        `/get-all-clients?sort_by=client_name&sort_order=asc&want_pagination=1&page=${pageNum}&per_page=${perPage}&search=${encodeURIComponent(
          debouncedSearchQuery
        )}`
      );
      const newClients = response.data.data || [];
      setClients((prev) => (reset ? newClients : [...prev, ...newClients]));
      setFilteredClients((prev) =>
        reset ? newClients : [...prev, ...newClients]
      );
      setCounts({
        total_client_count: response.data.counts.total_client_count || 0,
        branches_count: response.data.counts.total_branches_count || 0,
        locations_count: response.data.counts.total_locations_count || 0,
        slots_count: response.data.counts.total_slots_count || 0,
      });
      setHasMore(newClients.length === perPage);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError(true);
    }
    setLoadingClients(false);
    setLoadingMore(false);
    setIsSearching(false);
  };

  useEffect(() => {
    setPage(1);
    fetchClients(1, true);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingClients &&
          !loadingMore
        ) {
          setPage((prev) => prev + 1);
          fetchClients(page + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, loadingClients, loadingMore, page]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(true);
    setPage(1);
    setClients([]);
    setFilteredClients([]);
    setHasMore(true);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setPage(1);
    setClients([]);
    setFilteredClients([]);
    setHasMore(true);
  };

  const fetchBranches = async (clientId) => {
    setLoadingBranches(true);
    setError(null);
    try {
      const response = await apiCall(
        "get",
        `/client-branches/${clientId}?sort_by=branch_name&sort_order=asc&want_pagination=0`
      );
      setBranches(response.data.data);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setError(true);
    }
    setLoadingBranches(false);
  };

  const fetchLocations = async (branchId) => {
    setLoadingLocations(true);
    setError(null);
    try {
      const response = await apiCall(
        "get",
        `/get-branch-locations/${branchId}?sort_by=location_name&sort_order=asc&want_pagination=0`
      );
      setLocations(response.data.data || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
      setError(true);
    }
    setLoadingLocations(false);
  };

  const fetchSlots = async (locationId) => {
    setLoadingSlots(true);
    setError(null);
    try {
      const response = await apiCall(
        "get",
        `/get-slots-by-location-id/${locationId}?want_pagination=0`
      );
      setSlots(response.data.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setError(true);
    }
    setLoadingSlots(false);
  };

  const toggleClient = (clientId) => {
    const isExpanding = !expandedClient[clientId];
    if (isExpanding) {
      setExpandedClient({ [clientId]: true });
      fetchBranches(clientId);
      setExpandedBranch({});
      setExpandedLocation({});
      setLocations([]);
      setSlots([]);
    } else {
      setExpandedClient((prev) => ({ ...prev, [clientId]: false }));
      setBranches([]);
      setLocations([]);
      setSlots([]);
    }
  };

  const toggleBranch = (branchId) => {
    const isExpanding = !expandedBranch[branchId];
    if (isExpanding) {
      setExpandedBranch({ [branchId]: true });
      fetchLocations(branchId);
      setExpandedLocation({});
      setSlots([]);
    } else {
      setExpandedBranch((prev) => ({ ...prev, [branchId]: false }));
      setLocations([]);
      setSlots([]);
    }
  };

  const toggleLocation = (locationId) => {
    const isExpanding = !expandedLocation[locationId];
    if (isExpanding) {
      setExpandedLocation({ [locationId]: true });
      fetchSlots(locationId);
    } else {
      setExpandedLocation((prev) => ({ ...prev, [locationId]: false }));
      setSlots([]);
    }
  };

  // Popup for Workflows
  const renderWorkflowPopup = (location, locationId) => {
    if (!location.assigned_workflows?.length) return null;
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "workflow";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%",
          left: "0",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px",
          maxHeight: "150px",
          overflowY: "auto",
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <ul className="list-unstyled mb-0">
          {location.assigned_workflows.map((wf) => (
            <li key={wf.workflow_id} style={{ marginBottom: "4px" }}>
              {wf.workflow_name}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Popup for Staff
  const renderStaffPopup = (location, locationId) => {
    if (!location.assigned_staff?.length) return null;
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "staff";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%",
          left: "33.33%",
          transform: "translateX(-50%)",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px",
          maxHeight: "150px",
          overflowY: "auto",
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <ul className="list-unstyled mb-0">
          {location.assigned_staff.map((staff) => (
            <li key={staff.staff_id} style={{ marginBottom: "4px" }}>
              {staff.staff_name}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // Popup for Slots
  const renderSlotsPopup = (location, locationId) => {
    const isVisible =
      hoveredItem?.id === locationId && hoveredItem.type === "slots";

    return (
      <div
        className="position-absolute rounded popup-content"
        style={{
          backgroundColor: "#333",
          color: "white",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          bottom: "100%",
          left: "66.66%",
          transform: "translateX(-75%)",
          minWidth: "200px",
          zIndex: isVisible ? 9999 : -1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          marginBottom: "4px",
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? "visible" : "hidden",
        }}
      >
        <div className="d-flex align-items-center mb-6">
          <CheckCircle
            size={12}
            className="me-8"
            style={{ color: "#10b981" }}
          />
          <span style={{ color: "white" }}>
            Available: {location.open_slots || 0}
          </span>
        </div>
        <div className="d-flex align-items-center mb-6">
          <Car size={12} className="me-8" style={{ color: "#ef4444" }} />
          <span style={{ color: "white" }}>
            Occupied: {location.occupied_slots || 0}
          </span>
        </div>
        <div className="d-flex align-items-center">
          <Clock size={12} className="me-8" style={{ color: "#f59e0b" }} />
          <span style={{ color: "white" }}>
            Reserved: {location.reserved_slots || 0}
          </span>
        </div>
      </div>
    );
  };

  const ShimmerCard = ({ type }) => {
    const shimmerCount =
      type === "clients"
        ? 3
        : type === "branches"
        ? 2
        : type === "locations"
        ? 2
        : 4;
    return (
      <div className="row g-20">
        {Array(shimmerCount)
          .fill()
          .map((_, index) => (
            <div key={index} className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
              <div className="card border-0 shadow-sm radius-12 h-100">
                <div className="card-body p-20">
                  <div className="d-flex align-items-start gap-3">
                    <div className="placeholder-glow">
                      <div className="placeholder w-48-px h-48-px radius-8"></div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="placeholder-glow mb-2">
                        <span className="placeholder col-8"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  };

  if (error) {
    return <ErrorAlert />;
  }
  return (
    <div style={{ backgroundColor: "var(--bg-color)", minHeight: "100vh" }}>
      <div className="container-fluid">
        <CountsCard counts={counts} loading={loadingClients} />
      </div>

      <div className="container-fluid mb-24">
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm radius-12">
              <div className="card-body p-16">
                <div className="position-relative">
                  <div className="position-absolute top-50 start-0 translate-middle-y ps-16">
                    <Search size={20} className="text-secondary-light" />
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-lg ps-48 pe-48"
                    placeholder="Search clients by name..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <div className="position-absolute top-50 end-0 translate-middle-y pe-16">
                      <button
                        className="btn btn-sm btn-icon"
                        onClick={clearSearch}
                      >
                        <X size={16} className="text-secondary-light" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        {loadingClients && page === 1 ? (
          <ShimmerCard type="clients" />
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-48">
            <div className="w-80-px h-80-px bg-neutral-100 radius-12 d-flex justify-content-center align-items-center mx-auto mb-16">
              <Search size={32} className="text-secondary-light" />
            </div>
            <h5 className="text-secondary-light mb-8">No clients found</h5>
            <p className="text-secondary-light mb-0">
              {searchQuery
                ? `No clients match your search for "${searchQuery}"`
                : "No clients available"}
            </p>
          </div>
        ) : (
          <div className="clients-container">
            {filteredClients.map((client) => (
              <div key={client.client_id} className="client-card">
                <div
                  className="card border-lg shadow-lg radius-16 overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--primary-50) 0%, var(--primary-25) 100%)",
                    border: "none",
                  }}
                >
                  <div
                    className={`position-relative overflow-hidden bg-gradient-to-r from-primary-50 to-primary-25 cursor-pointer client-header ${
                      client.branch_count < 1 ? "opacity-60" : ""
                    }`}
                    style={{
                      background:
                        client.branch_count >= 1
                          ? "linear-gradient(135deg, var(--primary-50) 0%, var(--primary-25) 100%)"
                          : "var(--neutral-50)",
                      cursor:
                        client.branch_count >= 1 ? "pointer" : "not-allowed",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() =>
                      client.branch_count >= 1 && toggleClient(client.client_id)
                    }
                  >
                    <div className="card-body p-20">
                      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                        <div className="d-flex align-items-start gap-3 flex-grow-1 min-w-0">
                          <div className="w-48-px h-48-px bg-primary-600 radius-12 d-flex justify-content-center align-items-center position-relative overflow-hidden flex-shrink-0">
                            <div className="w-48-px h-48-px bg-primary-600 radius-12 d-flex justify-content-center align-items-center position-relative overflow-hidden flex-shrink-0">
                              {client.logo_url ? (
                                <img
                                  src={`https://pevbackend.codovio.com/public/${client.logo_url}`}
                                  alt={client.client_name}
                                  className="w-100 h-100"
                                  style={{ objectFit: "cover" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                style={{
                                  display: client.logo_url ? "none" : "flex",
                                }}
                                className="w-100 h-100 justify-content-center align-items-center bg-primary-600"
                              >
                                <span
                                  className="text-white fw-bold"
                                  style={{ fontSize: "18px" }}
                                >
                                  {client.client_name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)",
                                  pointerEvents: "none",
                                }}
                              ></div>
                            </div>
                            <div
                              className="position-absolute top-0 start-0 w-100 h-100"
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)",
                              }}
                            ></div>
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <p className="text-primary-light fw-bold mb-4 fs-16 text-wrap text-break">
                              {client.client_name}
                            </p>
                            <p className="text-secondary-light mb-8 fs-12 text-wrap text-break">
                              {client.email || "No email provided"}
                            </p>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge px-12 py-4 fs-12 fw-medium radius-6 ${
                              client.branch_count > 0
                                ? "bg-primary-600 text-white"
                                : "bg-neutral-300 text-neutral-600"
                            }`}
                          >
                            {client.branch_count > 0
                              ? `${client.branch_count} Branch${
                                  client.branch_count > 1 ? "es" : ""
                                }`
                              : "No Branches"}
                          </span>
                          {client.branch_count >= 1 && (
                            <button
                              className="w-28-px h-28-px radius-6 d-flex justify-content-center align-items-center flex-shrink-0"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleClient(client.client_id)}
                            >
                              {expandedClient[client.client_id] ? (
                                <ChevronDown
                                  size={16}
                                  className="text-primary-600"
                                  style={{ transition: "transform 0.3s ease" }}
                                />
                              ) : (
                                <ChevronRight
                                  size={16}
                                  className="text-primary-600"
                                  style={{ transition: "transform 0.3s ease" }}
                                />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedClient[client.client_id] && (
                    <div
                      className="branch-expansion"
                      style={{
                        animation: expandedClient[client.client_id]
                          ? "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                          : "none",
                        padding: "var(--size-20)",
                        background:
                          "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)",
                      }}
                    >
                      {loadingBranches ? (
                        <ShimmerCard type="branches" />
                      ) : branches.length > 0 ? (
                        branches.map((branch, index) => (
                          <div
                            key={branch.branch_id}
                            className="branch-card"
                            style={{
                              marginBottom:
                                index === branches.length - 1
                                  ? "0"
                                  : "var(--size-20)",
                              transition:
                                "transform 0.2s ease, box-shadow 0.2s ease",
                            }}
                          >
                            <div
                              className={`position-relative bg-gradient-to-r from-success-50 to-success-25 branch-header ${
                                branch.location_count < 1
                                  ? "opacity-60"
                                  : "cursor-pointer"
                              }`}
                              style={{
                                background:
                                  branch.location_count >= 1
                                    ? "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)"
                                    : "var(--neutral-50)",
                                transition: "all 0.2s ease",
                                borderRadius: "var(--rounded-12)",
                                padding: "var(--size-20)",
                              }}
                              onClick={() =>
                                branch.location_count >= 1 &&
                                toggleBranch(branch.branch_id)
                              }
                            >
                              <div className="d-flex align-items-start justify-content-between gap-3">
                                <div className="d-flex align-items-start gap-3 flex-grow-1 min-w-0">
                                  <div className="w-40-px h-40-px bg-success-600 radius-8 d-flex justify-content-center align-items-center flex-shrink-0">
                                    <Building
                                      size={18}
                                      className="text-white"
                                    />
                                  </div>
                                  <div className="flex-grow-1 min-w-0">
                                    <p className="text-primary-light fw-semibold mb-8 fs-16 text-wrap text-break">
                                      {branch.branch_name}
                                    </p>
                                    <p className="text-secondary-light mb-0 fs-12 text-wrap text-break">
                                      {branch.address || "No address provided"}
                                    </p>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    className={`badge px-12 py-4 fs-12 fw-medium radius-6 ${
                                      branch.location_count > 0
                                        ? "bg-primary-600 text-white"
                                        : "bg-neutral-300 text-neutral-600"
                                    }`}
                                  >
                                    {branch.location_count > 0
                                      ? `${branch.location_count} Location${
                                          branch.location_count > 1 ? "s" : ""
                                        }`
                                      : "No Locations"}
                                  </span>
                                  {branch.location_count >= 1 && (
                                    <button
                                      className="w-28-px h-28-px radius-6 d-flex justify-content-center align-items-center flex-shrink-0"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        toggleBranch(branch.branch_id)
                                      }
                                    >
                                      {expandedBranch[branch.branch_id] ? (
                                        <ChevronDown
                                          size={16}
                                          className="text-success-600"
                                        />
                                      ) : (
                                        <ChevronRight
                                          size={16}
                                          className="text-success-600"
                                        />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {expandedBranch[branch.branch_id] && (
                              <div
                                className="location-expansion"
                                style={{
                                  animation: expandedBranch[branch.branch_id]
                                    ? "slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                    : "none",
                                  padding: "var(--size-20)",
                                  background:
                                    "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)",
                                }}
                              >
                                <div className="d-flex justify-content-end mb-16">
                                  {/* Add location button can be added here if needed */}
                                </div>
                                {loadingLocations ? (
                                  <ShimmerCard type="locations" />
                                ) : locations.length > 0 ? (
                                  locations.map((location, index) => (
                                    <div
                                      key={location.location_id}
                                      className="location-card"
                                      style={{
                                        marginBottom:
                                          index === locations.length - 1
                                            ? "0"
                                            : "var(--size-20)",
                                        transition:
                                          "transform 0.2s ease, box-shadow 0.2s ease",
                                      }}
                                    >
                                      <div
                                        className={`position-relative bg-gradient-to-r from-success-50 to-success-25 location-header ${
                                          location.total_slots < 1
                                            ? "opacity-60"
                                            : ""
                                        }`}
                                        style={{
                                          background:
                                            location.total_slots >= 1
                                              ? "linear-gradient(135deg, var(--success-50) 0%, var(--success-25) 100%)"
                                              : "var(--neutral-50)",
                                          transition: "all 0.2s ease",
                                          borderRadius: "var(--rounded-12)",
                                          padding: "var(--size-20)",
                                        }}
                                      >
                                        <div className="d-flex align-items-start gap-3 flex-grow-1 position-relative">
                                          <div className="w-40-px h-40-px bg-success-600 radius-8 d-flex justify-content-center align-items-center flex-shrink-0">
                                            <MapPinned
                                              size={18}
                                              className="text-white"
                                            />
                                          </div>
                                          <div className="flex-grow-1 min-w-0">
                                            <p className="text-primary-light fw-semibold mb-12 fs-16 text-wrap text-break">
                                              {location.location_name}
                                            </p>
                                            <div className="d-flex gap-12">
                                              {/* Workflow Box */}
                                              <div
                                                className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative d-flex flex-column"
                                                onMouseEnter={() =>
                                                  handleHover(
                                                    location.location_id,
                                                    "workflow"
                                                  )
                                                }
                                                onMouseLeave={handleLeave}
                                              >
                                                <div className="cursor-pointer flex-grow-1">
                                                  <p className="fw-medium text-secondary-light mb-4 fs-12">
                                                    Workflows (
                                                    {location.assigned_workflows
                                                      ?.length || 0}
                                                    )
                                                  </p>
                                                </div>
                                                {renderWorkflowPopup(
                                                  location,
                                                  location.location_id
                                                )}
                                              </div>

                                              {/* Staff Box */}
                                              <div
                                                className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative d-flex flex-column"
                                                onMouseEnter={() =>
                                                  handleHover(
                                                    location.location_id,
                                                    "staff"
                                                  )
                                                }
                                                onMouseLeave={handleLeave}
                                              >
                                                <div className="cursor-pointer flex-grow-1">
                                                  <p className="fw-medium text-secondary-light mb-4 fs-12">
                                                    Staff (
                                                    {location.assigned_staff
                                                      ?.length || 0}
                                                    )
                                                  </p>
                                                </div>
                                                {renderStaffPopup(
                                                  location,
                                                  location.location_id
                                                )}
                                              </div>

                                              {/* Slots Box */}
                                              <div
                                                className="bg-white-alpha-20 p-12 rounded-8 flex-grow-1 position-relative"
                                                onMouseEnter={() =>
                                                  handleHover(
                                                    location.location_id,
                                                    "slots"
                                                  )
                                                }
                                                onMouseLeave={handleLeave}
                                              >
                                                <div className="cursor-pointer">
                                                  <p className="fw-medium text-secondary-light mb-4 fs-12">
                                                    Slots (
                                                    {location.total_slots || 0})
                                                  </p>
                                                </div>
                                                {renderSlotsPopup(
                                                  location,
                                                  location.location_id
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center">
                                    <p className="text-secondary-light fs-16 mb-16">
                                      No locations found
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center">
                          <p className="text-secondary-light fs-16 mb-16">
                            No branches found
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loadingMore && (
              <div className="text-center py-24">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            <div ref={observerRef} style={{ height: "10px" }} />
          </div>
        )}
      </div>

      <style jsx>{`
        :root {
          --size-4: 4px;
          --size-8: 8px;
          --size-12: 12px;
          --size-16: 16px;
          --size-20: 20px;
          --size-24: 24px;
          --size-32: 32px;
          --rounded-8: 8px;
          --rounded-12: 12px;
        }

        .clients-container {
          columns: 400px;
          column-gap: var(--size-24);
          column-fill: balance;
        }

        .client-card {
          break-inside: avoid;
          margin-bottom: var(--size-24);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .client-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .client-header:hover {
          background: linear-gradient(
            135deg,
            var(--primary-100) 0%,
            var(--primary-50) 100%
          ) !important;
        }

        .client-header .text-secondary-light.fs-12 {
          color: var(--neutral-600);
          line-height: 1.4;
        }

        .client-header .badge {
          margin-right: var(--size-8);
        }

        .text-wrap {
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
        }

        .text-break {
          word-break: break-all;
        }

        .bg-white-alpha-20 {
          background-color: rgba(255, 255, 255, 0.2);
        }

        .popup-content {
          pointer-events: none;
        }

        .btn-smaller {
          padding: 3px 6px !important;
          font-size: 11px !important;
          line-height: 1.5;
        }

        .btn-smaller .me-1 {
          margin-right: 4px;
        }

        @media (max-width: 575.98px) {
          .container-fluid {
            padding-left: var(--size-12);
            padding-right: var(--size-12);
          }

          .clients-container {
            columns: 100%;
          }

          .row.g-20 {
            gap: var(--size-12) !important;
            margin-left: calc(-1 * var(--size-12));
            margin-right: calc(-1 * var(--size-12));
          }

          .col-sm-12 {
            padding-left: var(--size-12);
            padding-right: var(--size-12);
          }

          .mb-24 {
            margin-bottom: var(--size-16) !important;
          }

          .py-48 {
            padding-top: var(--size-24) !important;
            padding-bottom: var(--size-24) !important;
          }

          .card-body {
            padding: var(--size-12) !important;
          }

          .branch-expansion,
          .location-expansion {
            padding: var(--size-12) !important;
          }

          .branch-card,
          .location-card {
            margin-bottom: var(--size-12) !important;
          }

          .branch-header,
          .location-header {
            padding: var(--size-12) !important;
            borderradius: var(--rounded-8) !important;
          }

          .fs-16 {
            font-size: 14px !important;
          }

          .fs-12 {
            font-size: 10px !important;
          }

          .fs-18 {
            font-size: 16px !important;
          }

          .w-80-px {
            width: 48px !important;
            height: 48px !important;
          }

          .w-50-px {
            width: 36px !important;
            height: 36px !important;
          }

          .w-48-px {
            width: 36px !important;
            height: 36px !important;
          }

          .w-40-px {
            width: 32px !important;
            height: 32px !important;
          }

          .w-28-px {
            width: 24px !important;
            height: 24px !important;
          }

          .text-2xl {
            font-size: 18px !important;
          }

          .form-control-lg {
            font-size: 12px !important;
            padding: 6px 28px !important;
          }

          .ps-48 {
            padding-left: 28px !important;
          }

          .pe-48 {
            padding-right: 28px !important;
          }

          .ps-16 {
            padding-left: 12px !important;
          }

          .pe-16 {
            padding-right: 12px !important;
          }

          .placeholder.w-48-px {
            width: 36px !important;
            height: 36px !important;
          }

          .badge {
            padding: 2px 8px !important;
            font-size: 10px !important;
          }

          .mb-6 {
            margin-bottom: 4px !important;
          }

          .me-8 {
            margin-right: 4px !important;
          }

          .popup-content {
            font-size: 10px !important;
            padding: 4px !important;
            minwidth: 150px !important;
          }

          .btn-smaller {
            font-size: 10px !important;
            padding: 2px 6px !important;
          }

          .text-wrap {
            font-size: 14px !important;
          }
        }

        @keyframes slideDown {
          from {
            max-height: 0;
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            max-height: 2000px;
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ClientManagement;
